import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Autocomplete from '@mui/material/Autocomplete';
import { useToast } from '@radiology/ui';
import { DocumentUpload } from 'iconsax-react';
import dayjs from 'dayjs';
import {
  ACCEPTED_TYPES,
  useMyCertificateOptions,
  useReplaceCertificate,
  useUploadCertificate,
  type Certificate,
} from './api';
import { useSession } from '../../auth/SessionContext';

/**
 * Upload a certificate, renew one, or replace the file on a rejected one.
 *
 * All three in one dialog because they are the same form — the legacy app had
 * two nearly identical components (`CertificateFormContainer` and
 * `UpdateCertificateContainer`) that drifted apart.
 *
 * They differ in exactly two ways:
 *
 *   `replacing`  a rejected certificate being re-submitted. Overwrites the row,
 *                and can carry a reply to the supervisor.
 *   `renewing`   a certificate TYPE being renewed. The type is fixed and cannot
 *                be edited (CERT-03), and it INSERTS a new version rather than
 *                touching the approved certificate it supersedes.
 *
 * Neither can be set at once.
 */
export function UploadDialog({
  open,
  replacing,
  renewing,
  onClose,
}: {
  open: boolean;
  replacing?: Certificate | null;
  /**
   * The certificate type being renewed, taken from the row the employee clicked
   * Renew on. Its presence is what locks the type field.
   */
  renewing?: string | null;
  onClose: () => void;
}) {
  const session = useSession();
  // CERT-11: only what applies to this employee — the All Titles list plus the
  // list mapped to their own Title. The previous dropdown offered every name in
  // the system to everyone.
  const options = useMyCertificateOptions();
  const upload = useUploadCertificate();
  const replace = useReplaceCertificate();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [expiryDate, setExpiryDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [comment, setComment] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reopening for a different certificate must not keep the previous one's
  // values — the legacy replace modal did, so a second edit could submit the
  // first certificate's expiry date.
  useEffect(() => {
    if (!open) return;
    setName(renewing ?? replacing?.name ?? '');
    // A renewal starts blank rather than at the old expiry date. CERT-03 asks
    // the employee for "the new expiry date", and pre-filling the date that has
    // just lapsed is an invitation to submit it unchanged.
    setExpiryDate(
      renewing
        ? ''
        : replacing?.expiry_date
          ? dayjs(replacing.expiry_date).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
    );
    setComment('');
    setFile(null);
    setError(null);
  }, [open, replacing, renewing]);

  const pending = upload.isPending || replace.isPending;

  const pick = (selected: File | null) => {
    setError(null);
    if (!selected) return;
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setError('Only PDF, JPEG, PNG and GIF files are accepted.');
      setFile(null);
      return;
    }
    // The bucket rejects anything larger; saying so here avoids a failed upload.
    if (selected.size > 15 * 1024 * 1024) {
      setError('That file is larger than 15 MB.');
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const submit = () => {
    if (!name.trim()) {
      setError('Choose which certificate this is.');
      return;
    }
    if (!file) {
      setError('A file is required.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) {
      setError('Enter a valid expiry date.');
      return;
    }

    // A renewal that does not move the expiry date forward is almost certainly
    // the old document uploaded again. The supervisor would catch it, but only
    // after it had sat in their queue.
    if (renewing && dayjs(expiryDate).isBefore(dayjs(), 'day')) {
      setError('The new expiry date is in the past. Check you have the current certificate.');
      return;
    }

    setError(null);
    const done = {
      onSuccess: () => {
        toast.success(
          replacing
            ? 'Certificate re-submitted for review'
            : renewing
              ? 'Renewal sent for review'
              : 'Certificate sent for review',
        );
        onClose();
      },
      onError: (e: Error) => setError(e.message),
    };

    if (replacing) {
      replace.mutate(
        {
          id: replacing.id,
          name: name.trim(),
          expiryDate,
          file,
          comment,
          existing: replacing.comment,
          previousPath: replacing.file_name,
          authorName: session.user.displayName ?? session.user.username,
        },
        done,
      );
    } else {
      upload.mutate({ name: name.trim(), expiryDate, file }, done);
    }
  };

  const lastComment = replacing?.comment?.[replacing.comment.length - 1];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h4">
        {replacing
          ? 'Replace this certificate'
          : renewing
            ? `Renew ${renewing}`
            : 'Upload a certificate'}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {replacing && lastComment && (
            <Alert severity="warning">
              <Typography variant="body2">
                Your supervisor sent this back: “{lastComment.comment}”
              </Typography>
            </Alert>
          )}

          {/* CERT-04, said plainly. Submitting a renewal is not the same as
              being compliant again, and the employee needs to know that before
              they close the dialog and assume they are done. */}
          {renewing && (
            <Alert severity="info">
              <Typography variant="body2">
                This goes to your supervisor for review. It only counts once they approve it — until
                then this certificate stays as it is.
              </Typography>
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {/* CERT-03: on a renewal the type comes from the row that was renewed
              and cannot be changed. Rendered as a disabled field rather than
              plain text so it is obvious WHICH certificate is being renewed —
              the employee needs to see it to know they clicked the right row. */}
          {renewing ? (
            <TextField
              label="Certificate"
              value={renewing}
              disabled
              helperText="Set by the certificate you are renewing."
            />
          ) : (
            <Autocomplete
              options={options.data ?? []}
              value={name || null}
              onChange={(_e, value) => setName(value ?? '')}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Certificate"
                  required
                  helperText={
                    // CERT-11 turned this into a closed list. It used to be
                    // freeSolo, which is how "xry-cert" and "test 1" got onto
                    // real certificate records in the first place.
                    options.data && options.data.length === 0
                      ? 'No certificates are configured for your title yet. Ask your administrator.'
                      : 'The certificates required for your title.'
                  }
                />
              )}
            />
          )}

          <TextField
            label={renewing ? 'New expiry date' : 'Expiry date'}
            type="date"
            required
            InputLabelProps={{ shrink: true }}
            value={expiryDate}
            onChange={e => setExpiryDate(e.target.value)}
            helperText="You are reminded 45 days before this, and again when it passes."
          />

          <Divider />

          <Stack gap={1}>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept={ACCEPTED_TYPES.join(',')}
              onChange={e => pick(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              onClick={() => fileInput.current?.click()}
              startIcon={<DocumentUpload size={18} color="currentColor" />}
            >
              {file ? 'Choose a different file' : 'Choose a file'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {file
                ? `${file.name} · ${Math.round(file.size / 1024)} KB`
                : 'PDF, JPEG, PNG or GIF.'}
            </Typography>
          </Stack>

          {replacing && (
            <TextField
              label="Reply to your supervisor"
              multiline
              minRows={2}
              value={comment}
              onChange={e => setComment(e.target.value)}
              helperText="Optional. Added to the certificate's history."
            />
          )}

          <Typography variant="caption" color="text.secondary">
            Filed as {session.user.displayName ?? session.user.username}
            {session.context.hospital ? ` · ${session.context.hospital}` : ''}
            {session.context.section ? ` · ${session.context.section}` : ''}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={pending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={pending}>
          {pending ? 'Uploading…' : replacing ? 'Re-submit' : 'Send for review'}
        </Button>
        {/* CERT-03 names this button "Send for review" for a renewal too — the
            renewal goes through the same review process as any other upload. */}
      </DialogActions>
    </Dialog>
  );
}
